import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { useNavigate } from "react-router";
import { useGlobalSearch, type SearchResult } from "../lib/use-global-search";

/**
 * Top-bar search across clients, pipelines and run ids (spec §5.5).
 *
 * ⌘K / Ctrl+K focuses it from anywhere; results group as Runs, Clients,
 * Pipelines; arrows move, Enter opens, Escape closes. A combobox rather than a
 * plain input, so `aria-activedescendant` can name the highlighted option to a
 * screen reader instead of leaving the movement silent.
 */
export function SearchBox() {
  const nav = useNavigate();
  const { query, setQuery, status, results, load } = useGlobalSearch();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  useEffect(() => {
    /** Cmd/Ctrl-K from anywhere in the shell puts the cursor here, text selected. */
    function onKey(e: globalThis.KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const flat: SearchResult[] = [
    ...(results.run ? [results.run] : []),
    ...results.clients,
    ...results.pipelines,
  ];
  const showList = open && query.trim().length > 0;

  /** Follow a result, closing the list and clearing the query behind it. */
  function go(r: SearchResult) {
    setOpen(false);
    setQuery("");
    nav(r.to);
  }

  /** Keyboard navigation over the result list: arrows move, Enter follows, Escape closes. */
  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, Math.max(flat.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      const r = flat[active];
      if (r) {
        e.preventDefault();
        go(r);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
    }
  }

  // Index offsets so the highlight walks Runs → Clients → Pipelines in order.
  const runOffset = 0;
  const clientOffset = results.run ? 1 : 0;
  const pipelineOffset = clientOffset + results.clients.length;

  // Stable per-item id (kind + id, never the array index) so a screen reader
  // can be told which option ArrowUp/ArrowDown just highlighted.
  const activeOption = showList ? flat[active] : undefined;
  const activeId = activeOption ? optionId(listId, activeOption) : undefined;

  return (
    <div className="search">
      <input
        ref={inputRef}
        className="search__input"
        type="search"
        role="combobox"
        aria-label="Search clients, pipelines and run ids"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={activeId}
        placeholder="Search"
        value={query}
        onFocus={() => {
          load();
          setOpen(true);
        }}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
      />
      <kbd className="search__hint" aria-hidden="true">⌘K</kbd>
      {showList ? (
        <div className="search__results" id={listId} role="listbox">
          {status === "failed" ? <div className="search__note">Search is unavailable right now</div> : null}
          {status === "loading" ? <div className="search__note">Loading</div> : null}
          {results.run ? (
            <Group listId={listId} title="Runs" items={[results.run]} offset={runOffset} active={active} onPick={go} />
          ) : null}
          <Group listId={listId} title="Clients" items={results.clients} offset={clientOffset} active={active} onPick={go} />
          <Group listId={listId} title="Pipelines" items={results.pipelines} offset={pipelineOffset} active={active} onPick={go} />
          {status === "ready" && flat.length === 0 ? <div className="search__note">No matches</div> : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The DOM id for one result, from the listbox's `useId()` plus the result's
 * own kind and id — stable across re-renders and unique wherever the result
 * lands in the list, which the index would not be.
 */
function optionId(listId: string, r: SearchResult): string {
  return `${listId}-option-${r.kind}-${r.id}`;
}

/**
 * One titled block of results. `offset` is where this block starts in the
 * flattened list, which is what keeps the arrow-key highlight walking Runs →
 * Clients → Pipelines in the order they are rendered.
 */
function Group({
  listId,
  title,
  items,
  offset,
  active,
  onPick,
}: {
  listId: string;
  title: string;
  items: SearchResult[];
  offset: number;
  active: number;
  onPick: (r: SearchResult) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div className="search__group">
      <div className="search__group-title uppercase-label">{title}</div>
      {items.map((r, i) => {
        const idx = offset + i;
        return (
          <div
            key={`${r.kind}-${r.id}`}
            id={optionId(listId, r)}
            role="option"
            aria-selected={idx === active}
            className={`search__option${idx === active ? " search__option--active" : ""}`}
            // mousedown, not click: the input's blur would close the list first.
            onMouseDown={(e) => {
              e.preventDefault();
              onPick(r);
            }}
          >
            <span className="search__label">{r.label}</span>
            {r.meta ? <span className="search__meta mono">{r.meta}</span> : null}
          </div>
        );
      })}
    </div>
  );
}
